import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { LocalData, CompleterService, CompleterData, CompleterItem } from 'ng2-completer';
import { Subscription } from "rxjs/Subscription";
import { ValidationErrors } from "@angular/forms/src/directives/validators";
import { Store } from "@ngrx/store";

import { AppState } from "@store/app-state";
import { EventsStore } from "@store/events";

import { GRADES } from "@constants/grades";
import { SCHOOLS } from "@constants/schools";
import { STATES } from "@constants/states";
import { GENDERS } from "@constants/genders";
import { ConstData } from "@constants/helpers";

import { Address } from "@models/address";
import { Contact } from "@models/contact";
import { Phone } from "@models/phone";
import { Event } from "@models/event";
import { CreditCard } from "@models/credit-card";

import { MatchingValidation } from "../validators/matching";
import { UniqueEmailAsyncValidation } from "../validators/unique-email-async";

import {
  POPULATE_EDITABLE_CREDIT_CARD,
  PURCHASE_TICKETS_AS_GUEST,
  CLEAR_EVENT_ERRORS
} from "@reducers/events";

@Component({
  selector: 'purchase-ticket-guest-form',
  templateUrl: './purchase-ticket-guest-form.pug',
})

export class PurchaseTicketGuestFormComponent implements OnInit, OnDestroy {

  public event: Event
  public errors: any
  public mainForm: FormGroup
  public student: Contact = new Contact()
  public parent: Contact = new Contact()
  public creditCard: CreditCard = new CreditCard()
  public hasFormBeenSubmitted: boolean = false

  public genderSearchStr: string
  public genders: ConstData[] = GENDERS
  public genderCompleter: CompleterData

  public schoolSearchStr: string
  public schools: ConstData[] = SCHOOLS
  public schoolCompleter: CompleterData

  public stateSearchStr: string
  public states: ConstData[] = STATES
  public stateCompleter: CompleterData

  public gradeSearchStr: string
  public grades: ConstData[] = GRADES
  public gradeCompleter: CompleterData

  public loading: boolean = false
  public terms: boolean = false
  public grade: string

  private eventsSubscription: Subscription

  constructor(
    private store: Store<AppState>,
    private fb: FormBuilder,
    private completerService: CompleterService,
    private uniqueEmailsAsyncValidation: UniqueEmailAsyncValidation,
  ) {}

  ngOnInit() {
    this.schoolCompleter =
      this.completerService.local(this.schools, "title", "title")

    this.stateCompleter =
      this.completerService.local(this.states, "value", "title")

    this.gradeCompleter =
      this.completerService.local(this.grades, "value", "title")

    this.genderCompleter =
      this.completerService.local(this.genders, "value", "title")

    this.setupMainForm()
    this.store.dispatch({ type: CLEAR_EVENT_ERRORS })
    this.store.dispatch({ type: POPULATE_EDITABLE_CREDIT_CARD, payload: new CreditCard() })

    this.eventsSubscription = this.store.select("eventsReducer").subscribe(
      (s: EventsStore) => {
        this.event = s.activeEvent
        this.errors = s.errors
        this.loading = s.loading
      })
  }

  setupMainForm() {
    this.student.phones.push(new Phone())
    this.parent.phones.push(new Phone())
    this.parent.addresses.push(new Address())

    this.mainForm = this.fb.group({
      studentFirstName:     [this.student.firstName, [Validators.required]],
      studentLastName:      [this.student.lastName, [Validators.required]],
      studentDob:           [this.student.dob, [Validators.required]],
      studentPhone:         [this.student.phones[0].value, [Validators.required]],
      studentEmail:         [this.student.email, [Validators.required], [this.uniqueEmailsAsyncValidation]],
      studentGender:        [this.student.gender, [Validators.required]],
      studentNewsletterSubscription: [true],
      grade:                [this.student.grade, [Validators.required]],
      parentFirstName:      [this.parent.firstName, [Validators.required]],
      parentLastName:       [this.parent.lastName, [Validators.required]],
      parentPhone:          [this.parent.phones[0].value, [Validators.required]],
      parentEmail:          [this.parent.email, [Validators.required], [this.uniqueEmailsAsyncValidation]],
      parentAddressLineOne: [this.parent.addresses[0].lineOne, [Validators.required]],
      parentAddressLineTwo: [this.parent.addresses[0].lineTwo],
      parentAddressSuburb:  [this.parent.addresses[0].suburb, [Validators.required]],
      parentAddressState:   [this.parent.addresses[0].state, [Validators.required]],
      parentAddressPostcode: [this.parent.addresses[0].postcode, [Validators.required]],
      creditCardHolder:     [this.creditCard.holder, [Validators.required]],
      creditCardCardNumber: [this.creditCard.cardNumber, [Validators.required, Validators.minLength(16), Validators.maxLength(19)]],
      creditCardExpiryMonth: [this.creditCard.expiryMonth, [Validators.required, Validators.minLength(2), Validators.maxLength(2)]],
      creditCardExpiryYear: [this.creditCard.expiryYear, [Validators.required, Validators.minLength(4), Validators.maxLength(4)]],
      creditCardCvv:        [this.creditCard.cvv, [Validators.required, Validators.minLength(3), Validators.maxLength(4)]],
      terms:                [this.terms, [Validators.requiredTrue]],
    },
    {
      validator: Validators.compose([
        MatchingValidation.studentAndParentEmailsDifferent,
      ])
    })
  }

  selectedSchool(e: CompleterItem) {
    if (e && e.originalObject) {
      this.student.school = (e.originalObject as ConstData).value
    }
  }

  selectedGrade(e: CompleterItem) {
    if (e && e.originalObject) {
      const value = (e.originalObject as ConstData).value
      this.mainForm.controls['grade'].setValue(value)
    }
  }

  selectedState(e: CompleterItem) {
    if (e && e.originalObject) {
      const value = (e.originalObject as ConstData).value
      this.mainForm.controls['parentAddressState'].setValue(value)
    }
  }

  selectedGender(e: CompleterItem) {
    if (e && e.originalObject) {
      const value = (e.originalObject as ConstData).value
      this.mainForm.controls['studentGender'].setValue(value)
    }
  }

  onSubmit({ value, valid }: { value: any, valid: boolean }) {
    this.hasFormBeenSubmitted = true

    if (valid) {
      this.populateModelsFromForm(value)
    }
  }

  populateModelsFromForm(value: any) {
    this.student.firstName = value.studentFirstName
    this.student.lastName = value.studentLastName
    this.student.dob = value.studentDob
    this.student.gender = value.studentGender
    this.student.phones[0].value = value.studentPhone
    this.student.phones[0].label = 'Mobile'
    this.student.email = value.studentEmail
    this.student.newsletterSubscription = value.studentNewsletterSubscription
    this.student.grade = value.grade

    this.parent.firstName = value.parentFirstName
    this.parent.lastName = value.parentLastName
    this.parent.dob = value.parentDob
    this.parent.phones[0].value = value.parentPhone
    this.parent.phones[0].label = 'Mobile'
    this.parent.email = value.parentEmail
    this.parent.newsletterSubscription = value.studentNewsletterSubscription
    this.parent.addresses[0].label = 'home'
    this.parent.addresses[0].lineOne = value.parentAddressLineOne
    this.parent.addresses[0].lineTwo = value.parentAddressLineTwo
    this.parent.addresses[0].suburb = value.parentAddressSuburb
    this.parent.addresses[0].state = value.parentAddressState
    this.parent.addresses[0].postcode = value.parentAddressPostcode

    const taIds =
      this.event.ticketAllocations
        .filter(ta => ta.uiState.isPurchasing)
        .map(ta => ta.id)

    const payload =
      Event.createPurchaseTicketAsGuestPayload(
        this.student, this.parent, taIds, this.creditCard
      )

    this.store.dispatch({ type: CLEAR_EVENT_ERRORS })
    this.store.dispatch({ type: PURCHASE_TICKETS_AS_GUEST, payload: payload })
  }

  getKeys(errors: any): string[] { return Object.keys(errors) }

  ngOnDestroy() {
    this.eventsSubscription.unsubscribe()
  }

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/components/public/purchase-ticket-guest-form.ts